/*
 * Quantity + add-to-cart control used on product listing cards.
 * Adds the chosen quantity to the cart in the background. With the cart drawer
 * enabled it slides the drawer open afterwards; otherwise it only refreshes the
 * cart icon and never navigates to the cart page.
 */
if (!customElements.get('card-add-to-cart')) {
  customElements.define(
    'card-add-to-cart',
    class CardAddToCart extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.input = this.querySelector('.card-qty__input');
        this.minusButton = this.querySelector('[name="minus"]');
        this.plusButton = this.querySelector('[name="plus"]');
        this.cartButton = this.querySelector('.card-qty__cart');
        this.message = this.querySelector('.card-qty__message');
        this.variantId = this.dataset.variantId;
        this.max = parseInt(this.dataset.max, 10) || Infinity;
        this.busy = false;

        // One click handler for the whole control (event delegation), so a tap
        // on the +/- icons or the cart icon always reaches the right action.
        this.addEventListener('click', (event) => {
          const button = event.target.closest('button');
          if (!button || !this.contains(button)) return;
          if (button === this.minusButton) {
            event.preventDefault();
            event.stopPropagation();
            this.setQuantity(this.quantity - 1);
          } else if (button === this.plusButton) {
            event.preventDefault();
            event.stopPropagation();
            this.setQuantity(this.quantity + 1);
          } else if (button === this.cartButton) {
            event.preventDefault();
            event.stopPropagation();
            this.add();
          }
        });
        this.input.addEventListener('change', () => this.setQuantity(this.quantity));
        this.input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          this.setQuantity(this.quantity);
          this.add();
        });

        this.setQuantity(this.quantity);
      }

      get quantity() {
        const value = parseInt(this.input.value, 10);
        return isNaN(value) || value < 1 ? 1 : value;
      }

      setQuantity(value) {
        value = Math.max(1, Math.min(this.max, value));
        this.input.value = value;
        this.minusButton.disabled = value <= 1;
        this.plusButton.disabled = value >= this.max;
        this.showMessage('');
      }

      showMessage(text) {
        if (!this.message) return;
        this.message.textContent = text || '';
        this.message.hidden = !text;
      }

      async add() {
        if (this.busy) return;
        this.busy = true;
        this.classList.add('is-loading');
        this.cartButton.setAttribute('aria-disabled', 'true');
        this.showMessage('');

        const drawer = document.querySelector('cart-drawer');
        const sections = ['cart-icon-bubble'];
        if (drawer) sections.push('cart-drawer');
        const addUrl = ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';
        const quantity = this.quantity;

        try {
          const response = await fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: Number(this.variantId),
              quantity: quantity,
              sections: sections.join(','),
              sections_url: window.location.pathname,
            }),
          });
          const data = await response.json();

          if (!response.ok || data.status) {
            this.showMessage(data.description || data.message || '');
            return;
          }

          if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
            // Re-renders the drawer + cart icon and slides the drawer open.
            if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(this.cartButton);
            drawer.renderContents(data);
          } else {
            this.updateSections(data, drawer);
          }

          try {
            if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'card-add-to-cart',
                productVariantId: this.variantId,
                cartData: data,
              });
            }
          } catch (e) {}

          this.setQuantity(1);
          this.classList.add('is-added');
          clearTimeout(this.addedTimeout);
          this.addedTimeout = setTimeout(() => this.classList.remove('is-added'), 1400);
        } catch (error) {
          console.error(error);
        } finally {
          this.busy = false;
          this.classList.remove('is-loading');
          this.cartButton.removeAttribute('aria-disabled');
        }
      }

      updateSections(data, drawer) {
        if (!data.sections) return;
        const parse = (html, selector) => new DOMParser().parseFromString(html, 'text/html').querySelector(selector);

        try {
          const bubble = document.getElementById('cart-icon-bubble');
          const html = data.sections['cart-icon-bubble'];
          if (bubble && html) {
            const el = parse(html, '.shopify-section');
            if (el) bubble.innerHTML = el.innerHTML;
          }
        } catch (e) {
          console.error(e);
        }

        try {
          const html = data.sections['cart-drawer'];
          if (drawer && html) {
            const target = document.querySelector('#CartDrawer');
            const el = parse(html, '#CartDrawer');
            if (target && el) target.innerHTML = el.innerHTML;
            drawer.classList.remove('is-empty');
            const inner = drawer.querySelector('.drawer__inner');
            if (inner) inner.classList.remove('is-empty');
          }
        } catch (e) {
          console.error(e);
        }
      }
    }
  );
}
